import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import apiClient from "../../utils/api";

function ParkingQRCode() {
  const { id } = useParams();
  const [parking, setParking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch parking data using the ID from URL params
  useEffect(() => {
    const fetchParkingData = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const token = localStorage.getItem('auth_token');
        const response = await apiClient.get(`accounts/parkings/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });        
        console.log('API response:', response.data.data);
        
        // Get parking data
        const parkingData = response.data.data;
        
        // Fetch Base64 QR code
        try {
          const qrResponse = await apiClient.get(`accounts/parkings/${id}/qrcode`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Add Base64 QR code to parking data
          if (qrResponse.data && qrResponse.data.base64_data) {
            parkingData.qrCodeBase64 = qrResponse.data.base64_data;
          }
        } catch (qrError) {
          console.error('Error fetching QR code:', qrError);
          // Continue with the original QR URL if Base64 fetch fails
        }
        
        setParking(parkingData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching parking data:', err);
        setError('Failed to fetch parking data');
        setLoading(false);
      }
    };    
    fetchParkingData();
  }, [id]);

  // Auto-print QR code when parking data is loaded
  useEffect(() => {
    if (!parking || !parking.qr_code_url) return;
    
    console.log('Printing QR code for parking:', parking);
    // Add a small delay to ensure the component is fully rendered
    setTimeout(() => {
      printQRCode();
    }, 500);
  }, [parking]);

  const printQRCode = async () => {
    if (!parking || !parking.qr_code_url) {
      console.error('No QR code URL available');
      return;
    }

    try {
      console.log('Preparing to print QR code');
      
      let base64Data;
      
      try {
        // Use the API client to get the Base64 encoded QR code directly from the backend
        const token = localStorage.getItem('auth_token');
        
        // Get QR code through API endpoint that returns Base64 data
        // Set responseType to 'json' to ensure we get JSON parsing
        const response = await apiClient.get(`accounts/parkings/${parking.id}/qrcode`, {
          headers: { 
            Authorization: `Bearer ${token}`
          },
          responseType: 'json'
        });
        
        console.log('Fetching Base64 QR code through API');
        
        // Check if response is properly structured JSON with base64_data field
        if (response.data && response.data.base64_data) {
          console.log('Response contains Base64 data');
          base64Data = response.data.base64_data;
        } else {
          // If response is not proper JSON, it might be binary data - convert it to Base64
          console.log('Response appears to be binary, converting to Base64');
          const binaryString = String.fromCharCode.apply(null, new Uint8Array(response.data));
          base64Data = btoa(binaryString);
        }
        
        console.log('QR code Base64 data received successfully');
      } catch (error) {
        console.error('Error fetching QR code data:', error);
        console.error('Failed to get QR code data from server');
        throw error;
      }
      
      console.log('QR code data fetched successfully');
      
      console.log('QR code base64 data prepared for printing');
      
      // Detect user agent for appropriate printing method
      const userAgent = navigator.userAgent.toLowerCase();
      const isAndroid = userAgent.indexOf("android") > -1;
      
      if (isAndroid) {
        // Use Intent scheme for Android devices
        androidPrintQR(base64Data);
      } else {
        // Use WebSocket for desktop/PC
        desktopPrintQR(base64Data);
      }
      
    } catch (error) {
      console.error('Error fetching or printing QR code:', error);
    }
  };

  const androidPrintQR = (base64Data) => {
    try {
      console.log('Base64 QR code data for Android:', base64Data);
      // Clean the base64 data - remove any prefixes if they exist
      const cleanBase64 = base64Data.replace(/^data:image\/(png|jpeg|jpg);base64,/, '');
      console.log('Cleaned Base64 QR code data:', cleanBase64);
      // According to RawBT documentation: rawbt:data:image/png;base64,data
      const qrDataUrl = `rawbt:data:image/png;base64,${cleanBase64}`;
      console.log('Printing QR code using RawBT data scheme');
      window.location.href = qrDataUrl;
    } catch (error) {
      console.error('Error in Android QR printing:', error);
    }
  };

  const desktopPrintQR = (base64Data) => {
    try {
      // RawBT WebSocket server runs on port 40213 by default
      const socket = new WebSocket("ws://127.0.0.1:40213/");
      
      // Set buffer type as mentioned in RawBT documentation
      socket.binaryType = "arraybuffer";
      
      socket.onerror = function(error) {
        console.error("WebSocket Error: Connection to RawBT server failed");
        console.error("WebSocket Error:", error);
        // Fallback to browser print dialog
        fallbackBrowserPrint();
      };
      
      socket.onopen = function() {
        console.log('WebSocket connected, sending QR code data');
        
        // Clean the base64 data if necessary
        const cleanBase64 = base64Data.replace(/^data:image\/(png|jpeg|jpg);base64,/, '');
        
        // Send QR code data using rawbt:data scheme for desktop
        const qrCommand = `rawbt:data:image/png;base64,${cleanBase64}`;
        socket.send(qrCommand);
        
        // Close the socket after sending
        setTimeout(() => {
          socket.close(1000, "QR code sent");
        }, 200);
        
        console.log('QR code sent to desktop RawBT server');
      };
      
      socket.onclose = function(event) {
        if (event.code !== 1000) {
          console.warn('WebSocket closed unexpectedly, trying browser print');
          fallbackBrowserPrint();
        }
      };
      
      // Timeout fallback
      setTimeout(() => {
        if (socket.readyState === WebSocket.CONNECTING) {
          socket.close();
          console.error('WebSocket connection timeout');
          fallbackBrowserPrint();
        }
      }, 5000);
      
    } catch (error) {
      console.error('Error connecting to desktop RawBT server');
      console.error('WebSocket error details:', error);
      fallbackBrowserPrint();
    }
  };

  const fallbackBrowserPrint = () => {
    console.log('Using browser print as fallback for QR code');
    // Create a new window with just the QR code for printing
    const printWindow = window.open('', '_blank');
    if (printWindow && parking) {
      // Use Base64 data directly if available
      const qrCodeSrc = parking.qrCodeBase64 
        ? `data:image/png;base64,${parking.qrCodeBase64.replace(/^data:image\/(png|jpeg|jpg);base64,/, '')}` 
        : (parking.qr_code_url || '');
      
      printWindow.document.write(`
        <html>
          <head>
            <title>QR Code - Ticket ${parking.ticket_number}</title>
            <style>
              body { margin: 0; padding: 20px; text-align: center; font-family: Arial, sans-serif; }
              .qr-container { margin: 20px auto; }
              .qr-image { width: 200px; height: 200px; }
              .ticket-info { margin: 20px 0; font-size: 14px; }
              @media print {
                body { margin: 0; padding: 10px; }
                .qr-image { width: 150px; height: 150px; }
              }
            </style>
          </head>
          <body>
            <div class="qr-container">
              <div class="ticket-info">
                <h3>Parking Ticket QR Code</h3>
                <p>Ticket No: ${parking.ticket_number || 'N/A'}</p>
                <p>Plate: ${parking.plate_number || 'N/A'}</p>
              </div>
              <img 
                id="qrImage"
                alt="QR Code" 
                class="qr-image"
                src="${qrCodeSrc}"
              />
            </div>
            <script>
              // Directly print since we already have the Base64 data
              window.onload = function() {
                // Check if image has loaded successfully
                const img = document.getElementById('qrImage');
                if (img.complete && img.naturalHeight !== 0) {
                  printAndClose();
                } else {
                  img.onload = printAndClose;
                  img.onerror = function() {
                    document.body.innerHTML += '<p style="color: red">Failed to load QR code. Please try again.</p>';
                  };
                }
                
                function printAndClose() {
                  setTimeout(function() {
                    window.print();
                    setTimeout(function() {
                      window.close();
                    }, 1000);
                  }, 500);
                }
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
    }
  };

  // Manual print trigger function
  const handleManualPrint = () => {
    if (parking && parking.qr_code_url) {
      printQRCode();
    }
  };

  // If loading, show loading indicator
  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading QR code data...</div>
      </div>
    );
  }

  // If error, show error message
  if (error) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
        <div>Error: {error}</div>
      </div>
    );
  }

  // If no parking data, show message
  if (!parking) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>No parking data available</div>
      </div>
    );
  }

  // If no QR code URL, show message
  if (!parking.qr_code_url) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>No QR code available for this parking ticket</div>
        <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
          Ticket No: {parking.ticket_number || 'N/A'}
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <style>
        {`
          .qr-print-container {
            max-width: 400px;
            margin: 0 auto;
            padding: 20px;
            border: 1px solid #ddd;
            border-radius: 8px;
            background: white;
          }
          .qr-image-display {
            width: 200px;
            height: 200px;
            margin: 20px auto;
            display: block;
            border: 1px solid #eee;
          }
          .parking-info {
            margin: 20px 0;
            font-size: 14px;
            color: #333;
          }
          .print-button {
            background: #007bff;
            color: white;
            border: none;
            padding: 10px 20px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 16px;
            margin: 10px;
          }
          .print-button:hover {
            background: #0056b3;
          }
          .print-status {
            margin: 10px 0;
            font-size: 12px;
            color: #666;
          }
          @media print {
            body * { visibility: hidden; }
            .qr-print-container, .qr-print-container * { visibility: visible; }
            .qr-print-container { position: absolute; left: 0; top: 0; width: 100%; }
            .print-button { display: none; }
          }
        `}
      </style>
      
      <div className="qr-print-container">
        <h2>Parking QR Code</h2>
        
        <div className="parking-info">
          <div><strong>Ticket No:</strong> {parking.ticket_number || 'N/A'}</div>
          <div><strong>Plate:</strong> {parking.plate_number || 'N/A'}</div>
          <div><strong>Date:</strong> {parking.parking_date || 'N/A'}</div>
          <div><strong>Time:</strong> {parking.parking_time || 'N/A'}</div>
        </div>
        
        <img 
          src={parking.qrCodeBase64 ? `data:image/png;base64,${parking.qrCodeBase64.replace(/^data:image\/(png|jpeg|jpg);base64,/, '')}` : parking.qr_code_url} 
          alt="Parking QR Code" 
          className="qr-image-display"
        />
        
        <div className="print-status">
          QR code will be automatically printed to thermal printer
        </div>
        
        <button 
          className="print-button" 
          onClick={handleManualPrint}
        >
          Print QR Code Again
        </button>
        
      </div>
    </div>
  );
}

export default ParkingQRCode;
